import React from 'react';
import { prioMeta, formatDatum, istUeberfaellig, unteraufgabenFortschritt } from './todoConstants';

/**
 * Karte einer einzelnen Aufgabe: Erledigt-Haken, Titel, Prioritaet, Faelligkeit,
 * Kategorie, Beschreibung und abhakbare Unteraufgaben.
 *
 * @param {{ todo: object, kategorie: object|null, onUpdate: (todo) => void,
 *   onEdit: (todo) => void, onDelete: (todo) => void }} props
 */
export default function TodoItem({ todo, kategorie, onUpdate, onEdit, onDelete }) {
  const prio = prioMeta(todo.prioritaet);
  const ueberfaellig = istUeberfaellig(todo);
  const { erledigt, gesamt } = unteraufgabenFortschritt(todo);

  const toggleErledigt = () => onUpdate({ ...todo, erledigt: !todo.erledigt });

  const toggleUnteraufgabe = (index) =>
    onUpdate({
      ...todo,
      unteraufgaben: todo.unteraufgaben.map((u, i) =>
        i === index ? { ...u, erledigt: !u.erledigt } : u
      ),
    });

  return (
    <div className={`rounded-xl border border-line bg-paper-soft p-4 transition-opacity ${todo.erledigt ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={todo.erledigt}
          onChange={toggleErledigt}
          className="mt-1 h-5 w-5 cursor-pointer accent-brand-600"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`font-medium text-ink ${todo.erledigt ? 'line-through' : ''}`}>
              {todo.titel}
            </span>
            <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wide ring-1 ${prio.badge}`}>
              {prio.label}
            </span>
            {todo.faelligkeitsdatum && (
              <span className={`font-mono text-xs ${ueberfaellig ? 'font-semibold text-loss' : 'text-ink-muted'}`}>
                📅 {formatDatum(todo.faelligkeitsdatum)}
              </span>
            )}
            {kategorie && (
              <span className="inline-flex items-center gap-1 font-mono text-xs text-ink-muted">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: kategorie.farbe }} />
                {kategorie.name}
              </span>
            )}
          </div>

          {todo.beschreibung && (
            <p className="mt-1 whitespace-pre-wrap text-sm text-ink-soft">{todo.beschreibung}</p>
          )}

          {gesamt > 0 && (
            <div className="mt-2 space-y-1">
              <p className="font-mono text-[11px] text-ink-muted">Unteraufgaben {erledigt}/{gesamt}</p>
              {todo.unteraufgaben.map((u, i) => (
                <label key={i} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={u.erledigt}
                    onChange={() => toggleUnteraufgabe(i)}
                    className="h-4 w-4 cursor-pointer accent-brand-600"
                  />
                  <span className={u.erledigt ? 'text-ink-muted line-through' : 'text-ink-soft'}>{u.titel}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={() => onEdit(todo)}
            className="rounded-lg px-2 py-1 text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
            aria-label="Bearbeiten"
          >
            ✎
          </button>
          <button
            type="button"
            onClick={() => onDelete(todo)}
            className="rounded-lg px-2 py-1 text-ink-muted transition-colors hover:bg-loss/10 hover:text-loss"
            aria-label="Löschen"
          >
            🗑
          </button>
        </div>
      </div>
    </div>
  );
}
